/* eslint-disable max-len */
/* globals EventUiManager */

//
// InstantEvents: the system for posting and retrieving events to the websrvs
//

import Context from "shared/util/Context";
import {
	OKEVENT,
	triggerOkEvent,
} from "shared/util/OkEventHelpers";
import {
	removeElementById,
	hideElementById,
	showElementById,
	IFrameInsertedAt,
} from "shared/util/query_util";
import range from "shared/util/range";
import { NanoCookie } from "legacy/Cookies";

const env = Context.get("env");
const port = Context.get("port");
const currentUserId = Context.get("userid");

// Converts a ProfileFields user into the user format
// the rest of the legacy event code depends on.
// (see: EventUiManager, EventNotification in shared/util)
const userToPerson = (user) => {
	if (!user) {
		return {};
	}

	return {
		"userid": user.userid,
		"screenname": user.username,
		"displayname": user.userinfo.displayname,
		"location": user.userinfo.location,
		"gender": user.userinfo.gender_letter,
		"orientation": user.userinfo.orientation,
		"age": user.userinfo.age,
		"match": user.percentages.match,
		"enemy": user.percentages.enemy,
		"thumbnail": user.thumbs[0]["225x225"],
		"is_online": user.online ? 1 : 0,
		"open_connection": user.online ? 1 : 0,
	};
};


// ========================================================================================
// Instant Events controller
// ----------------------------------------------------------------------------------------

const InstantEvents = {

	// CONFIG, CONSTS
	m_service_name: "/instantevents",
	m_comet_iframe_prefix_min: 1, // [[[ each window/tab has to visit its own subdomain. Start with this, numerically, prepended on the iframe_suffix (figured out below)]]]
	m_comet_iframe_prefix_max: 12, // [[[ ...and go up to this high.]]]
	m_comet_retry_if_all_prefixes_taken: 5, // [[[ seconds to retry if user has so many windows open, all subdomains taken]]]
	m_max_server_reply_time: 70, // [[[ we need to hold a prefix at least this long]]]
	m_safety_throttle_seconds: 5, // [[[ if a connection fails, don't call again in less than this time, for safety!]]]
	m_seconds_to_consider_new: 5, // [[[ if a message is message is less than this old to the server, we flash the IM window]]]
	m_seconds_to_stay_online: 1800, // [[[ After this much time, add the "offline=1" cgi flag, so the backend will only send us notifications ]]]
	m_min_wait_after_cb_fail: 10, // [[[ if websrv is restarting or other failure,, wait at least this long before opening a new connection]]]
	m_max_wait_after_cb_fail: 30, // [[[ maximum this long]]]
	m_odds_of_posting_self_timer: 0.1, // [[[ 0..1 ]]]
	m_message_age_before_considered_archive: 3600, // [[[ a message this many seconds old will show up, potentially, but be grayed, from their old conversation]]]
	m_emergency_reopen_every: 40000, // This has to be greater than the server-side response time or we'll reopen shit for the wrong reasons
	m_emergency_reopen_timeout: null,

	// VARS
	m_optional_params: {},
	m_is_a_popup: false,		// [[[ InstantEvents can be instantiated in a page just designed to deal with one partner screenname ]]]
	m_partner_screenname: "", // [[[ If a popup, the screenname of the partner dealt with ]]]
	m_script_obj_id: `instant_events_script_${parseInt(Math.random() * 1000000, 10)}`,
	m_comet_iframe_prefix: -1, // a number from the ranges specified above; will be prepended on suffix to build domain
	m_script_obj: null, // the script object we add to the page
	m_destination_div: false,
	m_last_connection: false,
	m_comet_iframe_suffix: "", // NOW SET BELOW. e.g. ".craig.dev.okcupid.com:2000/instantevents/instantevents_iframe.html",
	m_last_server_gmt_sync: 0,
	m_last_server_seqid_sync: 0,
	m_last_user_activity: new Date(), // the last time the user sent a message or did something to show they didn't leave window open
	m_pending_online_checks: [ ],
	m_has_done_initial_online_checks: false,
	m_send_timing_queue: {}, // we'll keep a queue of send times, and on callbacks make another ajax call to post how long it took

	m_turnedon: false, // encodes if the user has disabled IMs.

	m_has_had_emergency_restart: false,
	m_is_first_e_r_attempt: true,
	m_num_throttle_hits: 0,

	// for debuggung
	m_last_json_replies: [],

	// METHODS
	invoke(destinationDiv, optionalParams) {
		// user wants to start IM off.
		this.m_turnedon = !optionalParams || !optionalParams.off;

		if (optionalParams) {
			this.m_optional_params = optionalParams;
		}

		this.setCometIFrameSuffix();
		this.m_last_user_activity = new Date();
		this.m_destination_div = destinationDiv;

		// check if orbit UI needed
		if (this.m_destination_div) {
			EventUiManager.invoke(destinationDiv, this.m_optional_params);
		}

		this.performOnlineCheckNow();
		this.openConnection(true, false);
	},

	setCometIFrameSuffix() {
		if (env === "dev") {
			// In an ideal universe, this, too, would be prefixed with `-instant`.
			// But that doesn't work in dev, and this does, so here we are.
			// As of this writing, all knowledge as to why we have to ping different
			// subdomains on each tab has been lost.
			this.m_comet_iframe_suffix = `${window.location.host}/instantevents`;
		}
		else if (port) {
			this.m_comet_iframe_suffix = `-instant.okcupid.com:${port}/instantevents`;
		}
		else {
			this.m_comet_iframe_suffix = "-instant.okcupid.com/instantevents";
		}
	},

	getAPrefixLock() {
		this.m_comet_iframe_prefix = -1;

		range(this.m_comet_iframe_prefix_min, this.m_comet_iframe_prefix_max + 1).forEach((i) => {
			if (this.m_comet_iframe_prefix !== -1) {
				return;
			}

			const x = NanoCookie.get(`iframe_prefix_lock_${i}`);

			if (!x) {
				NanoCookie.set(`iframe_prefix_lock_${i}`, "1", { ms: 1000 * this.m_max_server_reply_time });
				this.m_comet_iframe_prefix = i;
			}
		});

		// if the prefix is still -1, clear all prefix locks and set to 1
		if (this.m_comet_iframe_prefix === -1) {
			range(this.m_comet_iframe_prefix_min, this.m_comet_iframe_prefix_max + 1).forEach((i) => {
				NanoCookie.remove(`iframe_prefix_lock_${i}`);
			});

			this.m_comet_iframe_prefix = 1;
		}
	},

	clearPrefixLock() {
		NanoCookie.set(`iframe_prefix_lock_${this.m_comet_iframe_prefix}`, "1", { ms: 1000 });
		this.m_comet_iframe_prefix = -1;
	},

	openConnection(isInitial, bypassThrottle) {
		if (this.m_turnedon === false) {
			return;
		}

		const d = new Date();
		const timeLeft = this.m_safety_throttle_seconds * 1000 - (d - this.m_last_connection);

		if (timeLeft > 0 && !bypassThrottle) {
			this.m_num_throttle_hits += 1;
			setTimeout(
				() => InstantEvents.openConnection(false, false),
				timeLeft + 250
			);

			return;
		}

		this.getAPrefixLock(); // figure out a subdomain to use

		if (this.m_comet_iframe_prefix === -1) {
			setTimeout(
				() => InstantEvents.openConnection(false, false),
				this.m_comet_retry_if_all_prefixes_taken * 1000
			);

			if (this.m_emergency_reopen_timeout) {
				clearTimeout(this.m_emergency_reopen_timeout);
				this.m_is_first_e_r_attempt = false;
			}

			return;
		}

		this.m_last_connection = new Date();

		// [[[ make sure a connection is still open in a minute or so ]]]
		if (this.m_emergency_reopen_timeout) {
			clearTimeout(this.m_emergency_reopen_timeout);
			this.m_is_first_e_r_attempt = false;
		}

		this.m_emergency_reopen_timeout = setTimeout(
			() => InstantEvents.emergencyReopenConnection(),
			this.m_emergency_reopen_every
		);

		// MK 2011/3/17 -- specify document.location.protocol so that pages that are requested over SSL
		// also request instantevents over SSL.

		let finalSrc = env === "dev"
			? `${document.location.protocol}//${this.m_comet_iframe_suffix}?random=${Math.random()}`
			: `${document.location.protocol}//${this.m_comet_iframe_prefix}${this.m_comet_iframe_suffix}?random=${Math.random()}`;

		// If it's been 30 minutes and they're gone, only fetch event notifications now
		if (this.secondsSinceLastUserActivity() > this.m_seconds_to_stay_online) {
			finalSrc += "&offline=1";
		}

		if (!isInitial) {
			finalSrc += `&server_seqid=${this.m_last_server_seqid_sync}`;
			finalSrc += `&server_gmt=${this.m_last_server_gmt_sync}`;

			// We need to guarantee shutdown JS in the iframe has to run.
			// Will only take a couple milliseconds, but there's no rush in DOM removal.
			const objectid = this.m_script_obj_id;
			setTimeout(() => {
				removeElementById(objectid);
			}, 2000);
		}

		this.m_script_obj_id = `instant_events_script_${parseInt(Math.random() * 1000000, 10)}`;
		this.m_script_obj = IFrameInsertedAt(document.body);
		this.m_script_obj.setAttribute("id", this.m_script_obj_id);
		this.m_script_obj.setAttribute("style", "display: none;");
		this.m_script_tag = this.m_script_obj.doc.createElement("script");
		this.m_script_tag.src = finalSrc;
		this.m_script_tag.type = "text/javascript";
		setTimeout(
			() => InstantEvents.m_script_obj.doc.body.appendChild(InstantEvents.m_script_tag),
			500
		);
	},

	// [[[
	// Normally a new connection is spawned from the cb of a previous one. But what if there's an
	// error of some kind?  Or a dropped connection?  so this is called 65ish seconds after any
	// connection open. But it shouldn't get called because every new connection keeps pushing back
	// the call to this
	// ]]]

	emergencyReopenConnection() {
		const d = new Date();

		if (window.util && window.util.updateStats) {
			window.util.updateStats("instant - ui - emergency restart", 1, "counter", "GZPpF7XECZ2Kv9Vz1sVeN2GcwGg=");

			if (this.m_is_first_e_r_attempt) {
				window.util.updateStats("instant - ui - emergency restart - on first", 1, "counter", "yp1SFIPrKRjS1W9qXkQHTZjSCuI=");
			}
		}


		const openConnections = NanoCookie.findRegExp(/iframe_prefix_lock_.*/).length;
		const reqTime = d.getTime() / 1000;

		const queryParams = `?instanteventswatch=1&openconnections=${openConnections}&reqtime=${reqTime}`;
		fetch(`/why${queryParams}`);

		this.m_has_had_emergency_restart = true;
		this.openConnection(false, true);
	},

	recordResponseForDebugging(response) {
		const rJSON = Object.toJSON(response);
		this.m_last_json_replies.push(rJSON);

		if (this.m_last_json_replies.length > 5) {
			this.m_last_json_replies =
				this.m_last_json_replies.splice(1, this.m_last_json_replies.length - 1);
		}
	},

	// [[[
	// This is not called back by a normal AJAX reply.
	// It's called directly with the response (eval'ed JSON) and responseText
	// (just the text not eval'ed) from the iframe, when it gets a reply.
	// ]]]
	openConnection_cb(response) {
		// this.recordResponseForDebugging(response);

		this.clearPrefixLock();

		if (this.m_turnedon === false) {
			return;
		}

		if (response.server_gmt) {
			this.m_last_server_gmt_sync = response.server_gmt;
		}

		if (response.server_seqid && response.server_seqid > 0) {
			this.m_last_server_seqid_sync = response.server_seqid;
		}

		if (response.events && response.events.length) {
			// [[[ Restarting the connection has to be in a random time
			// for IE race condition when multiple windows both get IM's and try
			// to open new connections at the same ms.]]]
			setTimeout(() => InstantEvents.openConnection(false, true), Math.random() * 1000);

			// Converts the new thing to the old thing.
			const peopleInfo = response.peopleInfo || {};
			const people = Object.values(peopleInfo).map(userToPerson);

			response.events.forEach((event) => {
				// global messaging "message received" event and "read receipt" events
				if (event.type === "message_event") {
					const eventObject = {
						// We always have one of these.
						// The one we don't have is the currentUserId.
						from: event.from_userid || currentUserId,
						to: event.to_userid || currentUserId,
					};

					const contents = JSON.parse(event.contents);

					if (contents.message) {
						eventObject.message = {
							...contents.message,
							...eventObject,
						};

						// Patch in info about the other user.
						const correspondentId = event.from_userid || event.to_userid;
						eventObject.correspondent = peopleInfo[correspondentId];

						triggerOkEvent(OKEVENT.ReceivedMessage, eventObject);
					} else if (contents.remote_read) {
						eventObject.msgid = contents.remote_read.msgid;
						eventObject.from = contents.remote_read.userid;
						triggerOkEvent(OKEVENT.ReceivedReadReceipt, eventObject);
					}


				// [[[
				// User just added spotlight, and IE sent us
				// a blank notification to signal it
				// ]]]
				} else if (event.type === "new_spotlight_user" && !people.length) {


				// [[[	This if isn't really necessary, since once you turn off non-IM events, the iframes append the param "types=im",
				//		preventing any non-IM messages from being sent to the user anyway ]]]
				} else {
					people.forEach((person) => {
						let peopleFrom;
						let eventsFrom;

						if (typeof person.screenname !== "undefined") {
							peopleFrom = person.screenname.toLowerCase();
						}

						if (typeof event.from !== "undefined") {
							eventsFrom = event.from.toLowerCase();
						}

						if (peopleFrom && eventsFrom && peopleFrom === eventsFrom) {
							const isSignoffEvent = (event.type === "orbit" && event.event_id === "11" || event.type === "orbit_user_signoff");

							// [[[ If ((event is an old orbit logout || event is a new orbit logout) && event is from someone we're not chatting with) ]]]
							if (isSignoffEvent) {
								// [[[ Do nothing ]]]
								// [[[ The "old orbit" cases above can be removed after the new orbit types are live.  This temporary code handles both cases. ]]]
							} else if (this.m_destination_div) {
								EventUiManager.update(person, event);
								return;
							}
						}
					});
				}
			});
		} else if (response.not_logged_in) {
			this.m_turnedon = false;
		} else {
			// [[[ Restarting the connection has to be in a random time for IE
			// race condition when multiple windows both get IM's and try to
			// open new connections at the same ms.]]]
			setTimeout(
				() => InstantEvents.openConnection(false, false),
				Math.random() * 1000
			);
		}
	},

	endConnections() {
		this.m_turnedon = false;
		removeElementById(this.m_script_obj_id);
		clearTimeout(this.m_emergency_reopen_timeout);

		this.clearPrefixLock();
	},

	// [[[ checkOnline(params):
	//
	// If called before page load, it queues up, and later calls all of them at once
	// collected across the page. So there's only one ajax call, even if you have
	// 20 buttons around the page.
	//
	// If you call this after the page has loaded, for example, if you need to look up
	// someone's online info after the fact, it will execute immediately.
	//
	// How versatile.
	//
	// Params:
	//
	// 1. At least one of:
	//    a. username
	//    b. userid
	// 2. A subset of:
	//    a. cb : a function to call with the data about the user
	//    c. show : id to display:; if user is online
	//    b. hide : id to display:none; if user is online
	//
	// Example: InstantEvents.checkOnline({ username : 'jennieDT', show : 'im_jennieDT' });
	// Example: InstantEvents.checkOnline({ username : 'jennieDT', cb (response){ if (response.im_ok) alert(" you can IM her!") } })'
	//
	// Multiple online checks for the same user can happen on a page before pageload; all of the collective shows, hides, and cb's will be performed.
	// This makes sense since the user might be in a match result and in your favorites list, for example.
	//
	//
	// ]]]
	checkOnline(params) {
		this.m_pending_online_checks.push(params);

		if (this.m_has_done_initial_online_checks) {
			this.performOnlineCheckNow();
		}
	},

	performOnlineCheckNow() {
		this.m_has_done_initial_online_checks = true;

		if (this.m_pending_online_checks.length === 0) {
			return;
		}

		const params = {
			is_online: 1,
			rand: Math.random()
		};
		const addedAlready = {};
		let usernameList = "";
		let useridList = "";

		this.m_pending_online_checks.forEach((check) => {
			if (check.username && !addedAlready[check.username]) {
				if (usernameList !== "") {
					usernameList += ",";
				}

				usernameList += check.username;
				addedAlready[check.username] = true;
			} else if (check.userid && !addedAlready[check.userid]) {
				if (useridList !== "") {
					useridList += ",";
				}

				useridList += check.userid;
				addedAlready[check.userid] = true;
			}
		});

		if (usernameList !== "") {
			params.usernames = usernameList;
		}

		if (useridList !== "") {
			params.userids = useridList;
		}

		const options = {
			method: "POST",
			body: JSON.stringify(params),
			headers: {
				"Content-Type": "application/json",
			},
		};

		fetch(this.m_service_name, options)
			.then((response) => response.json())
			.then(this.performOnlineCheck_cb.bind(this))
			.catch(this.performOnlineCheck_cb_fail.bind(this));
	},

	performOnlineCheck_cb(results) {
		this.m_pending_online_checks.forEach((check) => {
			let foundYet = false;

			// TODO: n^2 to be fixed later but no big deal on this size anyway.
			results.forEach((result) => {
				if (
					(check.username && check.username.toLowerCase() === result.screenname.toLowerCase()) ||
					(check.userid === result.userid)
				) {
					foundYet = true;

					// [[[ TODO: REPLACE THIS WITH im_ok INSTEAD OF open_connection ]]]
					if (result.open_connection) {
						if (check.hide) {
							hideElementById(check.hide);
						}

						if (check.show) {
							showElementById(check.show);
						}
					}

					if (check.cb) {
						check.cb(result);
					}
				}
			});

			if (!foundYet) {
				check.cb({
					screenname: check.username.toLowerCase(),
					userid: check.userid,
					open_connection: false,
					im_ok: false,
					im_not_ok: true,
				});
			}
		});

		this.m_pending_online_checks = [];
	},

	blockImAjax(screenname) {
		const queryParams = `?im_block=1&target_screenname=${screenname}`;
		fetch(this.m_service_name + queryParams);
	},

	performOnlineCheck_cb_fail() {
		console.error("Failed to check online.");
	},

	updateLastUserActivity() {
		this.m_last_user_activity = new Date();
	},

	secondsSinceLastUserActivity() {
		const d = new Date();
		return (d.getTime() - this.m_last_user_activity.getTime()) / 1000;
	},
};

window.InstantEvents = InstantEvents;
export default InstantEvents;

// document.domain="okcupid.com";
document.addEventListener("onbeforeunload", InstantEvents.endConnections.bind(InstantEvents));



// WEBPACK FOOTER //
// ./src/shared/util/InstantEvents.js